import { useMemo, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Icon } from '@wartek-id/icon-v2'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import {
  useAPIDeleteKinerja,
  useAPIKinerja,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { Detail } from 'app/KinerjaGuruPage/components/perencanaan/Detail'
import { resolvePerencanaanDetailData } from 'app/KinerjaGuruPage/utils/perencanaan'
import { assetUrl } from 'utils/url'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'

import type { KinerjaStatus } from 'types/kinerja'
import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'

const ALLOWED_STATES: Array<KinerjaStatus> = ['APPROVED']

export interface SepakatiPerencanaanProps {
  isUlangiPerencanaanEnabled?: boolean
}

export function SepakatiPerencanaan({
  isUlangiPerencanaanEnabled,
}: SepakatiPerencanaanProps) {
  const router = useRouter()
  const { trackEvent } = useSKPTrackEvent()
  const toast = useToast()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const {
    data: perencanaanData,
    isLoading,
    isSuccess,
  } = useAPIKinerja(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
    {
      tipe: 'SPF',
    }
  )

  const perencanaanId = perencanaanData?.data?.id

  const resolvedPerencanaanData: PerencanaanGuruDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  const allowedToVisit = useMemo(() => {
    return ALLOWED_STATES.includes(perencanaanData?.data?.status)
  }, [perencanaanData?.data?.status])

  // -----------------------------------------
  // Perencanaan state guard
  // -----------------------------------------
  useEffect(() => {
    if (isSuccess && !allowedToVisit) {
      router.replace(PENGELOLAAN_KINERJA_GURU_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allowedToVisit, isSuccess])

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------

  const [activeAction, setActiveAction] = useState<
    'SEPAKATI' | 'ULANGI' | 'IDLE'
  >('IDLE')

  const handleOpenSepakatiDialog = () => {
    setActiveAction('SEPAKATI')
  }
  const handleOpenUlangiDialog = () => {
    setActiveAction('ULANGI')
  }
  const handleCloseDialog = () => {
    setActiveAction('IDLE')
  }

  const sepakatiMutation = useAPIUpdateState()
  const ulangiMutation = useAPIDeleteKinerja()

  const handleConfirmSepakati = () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_AGREEMENT',
      screen_name: 'KINERJA_PLANNING_AGREEMENT_POP_UP',
      kinerja_planning_id: perencanaanId,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    sepakatiMutation.mutate(
      {
        skpId: perencanaanId,
        tipe: 'SPF',
      },
      {
        onError() {
          toast({
            message: 'Gagal sepakati perencanaan. Coba lagi.',
          })
        },
        onSuccess() {
          toast({
            type: 'success',
            message: 'Berhasil sepakati Perencanaan.',
          })

          router.push(PENGELOLAAN_KINERJA_GURU_PATH)
        },
      }
    )
  }

  const handleCancelSepakati = () => {
    if (isUlangiPerencanaanEnabled) {
      handleOpenUlangiDialog()
    } else {
      handleCloseDialog()
    }
  }

  const handleConfirmUlangi = () => {
    ulangiMutation.mutate(
      {},
      {
        onError() {
          toast({
            message: 'Perencanaan gagal dihapus. Coba lagi.',
          })
        },
        onSuccess() {
          toast({
            type: 'success',
            message: 'Berhasil hapus Perencanaan.',
          })

          router.push(PENGELOLAAN_KINERJA_GURU_PATH)
        },
      }
    )
  }

  return (
    <>
      <main className="max-w-7xl mx-auto py-12 px-6">
        <Header title="Perencanaan Kinerja" className="mb-9" />

        <div className="bg-white rounded-lg">
          <div className="py-9 px-6">
            <section className="space-y-4 mb-10">
              <div className="flex items-center gap-3">
                <Icon className="!text-4xl !w-9 !h-9 !leading-9">
                  account_circle_outlined
                </Icon>
                <Text variant="heading-lg" as="h2">
                  {perencanaanData?.data?.nama}
                </Text>
              </div>
            </section>

            <Detail
              isLoading={isLoading}
              data={resolvedPerencanaanData}
              showReview={true}
            />
          </div>

          <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
            <Button color="blue" size="lg" onClick={handleOpenSepakatiDialog}>
              Sepakati Perencanaan
            </Button>
          </div>
        </div>
      </main>

      <SepakatiDialog
        isUlangiEnabled={isUlangiPerencanaanEnabled}
        isOpen={activeAction === 'SEPAKATI'}
        isLoading={sepakatiMutation.isLoading}
        onClose={handleCloseDialog}
        onCancel={handleCancelSepakati}
        onConfirm={handleConfirmSepakati}
      />

      <UlangiDialog
        isOpen={activeAction === 'ULANGI'}
        isLoading={ulangiMutation.isLoading}
        onClose={handleCloseDialog}
        onCancel={handleCloseDialog}
        onConfirm={handleConfirmUlangi}
      />
    </>
  )
}

interface SepakatiDialogProps extends Omit<DialogProps, 'children'> {
  isLoading?: boolean
  isUlangiEnabled?: boolean
  onConfirm?: () => void
  onCancel?: () => void
}

const SepakatiDialog = ({
  isLoading,
  isUlangiEnabled = false,
  onConfirm = () => {},
  onCancel = () => {},
  ...props
}: SepakatiDialogProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false)

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="dialog-sign-skp-title"
        aria-describedby="dialog-sign-skp-description"
      >
        <DialogCloseButton data-testid="btn-close" />
        <div className="mt-12 p-6">
          <img
            alt=""
            src={assetUrl('/illustrations/img-handshake.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />
          <Text
            variant="display-sm"
            className="mt-4"
            as="h2"
            id="dialog-sign-skp-title"
          >
            Konfirmasi untuk Sepakati Perencanaan
          </Text>
          <Text
            color="subdued"
            className="mt-4 !text-xl"
            as="p"
            id="dialog-sign-skp-description"
          >
            {isUlangiEnabled
              ? 'Perencanaan akan otomatis dikirim ke E-Kinerja dalam 1x24 jam. Jika ada yang ingin diubah, Anda dapat ulangi Perencanaan dari awal.'
              : `Perencanaan akan otomatis dikirim ke E-Kinerja dalam 1x24 jam.`}
          </Text>

          <div className="mt-4">
            <Checkbox
              checked={isConfirmed}
              disabled={isLoading}
              onChange={(e) => setIsConfirmed(e.target.checked)}
            >
              Saya sepakat dengan Perencanaan yang disetujui oleh Kepala
              Sekolah.
            </Checkbox>
          </div>

          <div className="flex gap-5 mt-5">
            <Button
              size="lg"
              color="white"
              disabled={isLoading}
              onClick={onCancel}
            >
              {isUlangiEnabled ? 'Ulangi dari awal' : 'Cek ulang'}
            </Button>
            <Button
              size="lg"
              disabled={isLoading || !isConfirmed}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const CONFIRMATION_TEXT =
  'Saya menyatakan bahwa saya menyetujui untuk mengulang perencanaan dari awal.'

interface UlangiDialogProps extends Omit<DialogProps, 'children'> {
  isLoading?: boolean
  onConfirm?: () => void
  onCancel?: () => void
}

const UlangiDialog = ({
  isLoading,
  onConfirm = () => {},
  onCancel = () => {},
  ...props
}: UlangiDialogProps) => {
  const [confirmationText, setConfirmationText] = useState('')
  const isConfirmed = confirmationText === CONFIRMATION_TEXT

  // Reset confirmation text when dialog is closed
  useEffect(() => {
    if (!props.isOpen) {
      setConfirmationText('')
    }
  }, [props.isOpen])

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="dialog-ulangi-title"
        aria-describedby="dialog-ulangi-description"
      >
        <DialogCloseButton data-testid="btn-close" />
        <div className="pt-12 p-6">
          <img
            alt=""
            src={assetUrl('/images/errors/img-error-fixing.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />
          <Text
            variant="display-sm"
            className="mt-4"
            as="h2"
            id="dialog-ulangi-title"
          >
            Hapus perencanaan Anda dan ulang dari awal?
          </Text>
          <Text
            color="subdued"
            variant="body-lg"
            className="mt-4"
            as="p"
            id="dialog-ulangi-description"
          >
            Jika lanjut, perencanaan yang Anda sudah lakukan akan terhapus dan
            Anda harus mengulang perencanaan dari awal.
          </Text>
          <Text color="subdued" variant="body-lg" className="mt-4">
            Mohon ketik kalimat ini ke kolom kalimat persetujuan:
          </Text>

          <div className="bg-surface-selected-default p-1 my-4">
            <Text color="subdued" variant="body-lg">
              {CONFIRMATION_TEXT}
            </Text>
          </div>

          <Text className="mb-1">Kalimat persetujuan</Text>
          <Textarea
            value={confirmationText}
            placeholder="Ketik di sini."
            onChange={(e) => setConfirmationText(e.target.value)}
          />

          <div className="flex gap-5 mt-5">
            <Button
              size="lg"
              disabled={isLoading || !isConfirmed}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Lanjut hapus
            </Button>
            <Button
              size="lg"
              color="white"
              disabled={isLoading}
              onClick={onCancel}
            >
              Kembali
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
